import { useQueryClient } from "@tanstack/react-query";
import { doc, getDoc } from "firebase/firestore";
import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import AutoFixHighRoundedIcon from "@mui/icons-material/AutoFixHighRounded";
import {
  Badge,
  Box,
  Button,
  Fade,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";

import { StoryJob, StoryJobStatus, isTranslationJob } from "@baiki/interfaces";

import { useConfetti, useSnackbar } from "@/hooks";
import { db } from "@/lib/firebase";
import { useAuth } from "@/providers";
import { JobsQueries } from "@/queries";

import { useComposeFlow } from "../compose";

export const AccountJobsCompose: React.FC = () => {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const queryClient = useQueryClient();
  const { breakpoints } = useTheme();
  const isLargeScreen = useMediaQuery(breakpoints.up("lg"));
  const { celebrate } = useConfetti();
  const { user } = useAuth();
  const { data: jobs = [] } = JobsQueries.useCustomerActiveJobs(user?.uid);
  const { enterCompose, composeFlow } = useComposeFlow();

  // Track previous active job IDs to detect completions/failures by disappearance
  const prevActiveJobIdsRef = useRef<string[]>([]);

  const jobLabel = useCallback(
    (job: StoryJob) =>
      isTranslationJob(job.input)
        ? t("app.flows.translate.jobLabel", {
            language: t(`languages.${job.input.language}`),
            title: job.input.sourceTitle,
          })
        : [job.input.who, job.input.what].filter(Boolean).join(" — "),
    [t],
  );

  useEffect(() => {
    if (!user || !db) {
      prevActiveJobIdsRef.current = [];
      return;
    }

    const currentIds = jobs.map((j) => j.id);
    const prevIds = prevActiveJobIdsRef.current;

    if (prevIds.length > 0) {
      // Jobs that disappeared from the active list have reached a terminal state
      const disappearedIds = prevIds.filter((id) => !currentIds.includes(id));

      for (const jobId of disappearedIds) {
        getDoc(doc(db, "jobs", jobId)).then((snapshot) => {
          if (!snapshot.exists()) return;
          const job = snapshot.data() as Pick<StoryJob, "input" | "progress">;
          const status = job.progress?.status;
          const isTranslation = isTranslationJob(job.input);

          if (status === StoryJobStatus.COMPLETED) {
            if (isTranslation) {
              snackbar.success(
                t("notifications.translationReady", {
                  language: t(`languages.${job.input.language}`),
                }),
              );
              // The pipeline writes the finished version into every group
              // member's `translations` map; story pages read their doc once,
              // so they need a refetch before the switch knows about it.
              queryClient.invalidateQueries({ queryKey: ["story"] });
            } else {
              // Confetti belongs to a story that did not exist before — a
              // second language of one you already have doesn't get a parade.
              snackbar.success(t("notifications.storyReady"));
              celebrate();
            }
          } else if (status === StoryJobStatus.FAILED) {
            snackbar.error(
              t(
                isTranslation
                  ? "notifications.translationFailed"
                  : "notifications.storyFailed",
              ),
            );
          }
        });
      }
    }

    prevActiveJobIdsRef.current = currentIds;
  }, [jobs, user, t, snackbar, celebrate, queryClient]);

  // One job at a time: ANY running job takes the compose entry away, a
  // translation as much as a story. (It used to be stories only, on the theory
  // that a running translation gives you nothing to compose against — but both
  // kinds occupy the same pipeline and cost the same 5 sparkles, so both wait
  // their turn.) The spinner beside it is what's left, and is the explanation.
  const showCompose = isLargeScreen && jobs.length === 0;

  if (!user || (jobs.length === 0 && !showCompose)) return null;

  return (
    <Stack alignItems="center" direction="row" spacing={2}>
      {jobs.length > 0 ? (
        <Tooltip
          title={
            <Stack spacing={0.5}>
              <Typography variant="caption">
                {t("common.topbar.jobsTooltip")}
              </Typography>
              {jobs.map((job) => (
                <Typography fontWeight={600} key={job.id} variant="caption">
                  {jobLabel(job)}
                </Typography>
              ))}
            </Stack>
          }
        >
          <Fade in={jobs.length > 0}>
            <Badge badgeContent={jobs.length} color="primary">
              <Box sx={{ position: "relative", display: "inline-flex" }}>
                <CircularProgress enableTrackSlot size="22px" />
                <Box
                  sx={{
                    alignItems: "center",
                    bottom: 0,
                    display: "flex",
                    justifyContent: "center",
                    left: 0,
                    position: "absolute",
                    right: 0,
                    top: 0,
                  }}
                >
                  <AutoAwesomeRoundedIcon
                    color="primary"
                    sx={{ fontSize: "12px" }}
                  />
                </Box>
              </Box>
            </Badge>
          </Fade>
        </Tooltip>
      ) : null}
      {showCompose ? (
        <>
          <Button
            onClick={enterCompose}
            size="small"
            startIcon={<AutoFixHighRoundedIcon />}
            variant="text"
          >
            {t("app.flows.compose.navItem")}
          </Button>
          {composeFlow}
        </>
      ) : null}
    </Stack>
  );
};
